import {
  signInWithTelegram,
  linkTelegram,
} from "@/shared/api/generated/auth-controller/auth-controller";
import { refreshAccessToken, session, setSignInFallback } from "@/shared/api/session";
import { isTelegram, loadTelegram, telegramInitData } from "@/shared/platform";

/**
 * Restores the session at startup. On the web: the refresh cookie. Inside the Telegram Mini App the customer whose
 * Telegram account is linked is signed in by the launch data first, without an SMS (ADR-0009 in the platform); an
 * unlinked one (or a disabled bot on the server) falls back to the cookie and, if needed, the usual SMS login. The
 * session stays «unknown» until this settles, so protected pages wait instead of sending to the login.
 */
export async function restoreSession(): Promise<void> {
  if (isTelegram()) {
    await loadTelegram();
    if (await signInByLaunchData()) {
      // Telegram's web client frames the store cross-site and drops the refresh cookie: later refreshes fall back
      // to the launch data too
      setSignInFallback(signInByLaunchData);
      return;
    }
  }
  await refreshAccessToken();
}

/** True when the launch data signed a linked customer in; false when not linked, expired or switched off. */
async function signInByLaunchData(): Promise<boolean> {
  const initData = telegramInitData();
  if (!initData) return false;
  try {
    const tokens = await signInWithTelegram({ as_init_data: initData });
    session.signIn(tokens.as_access_token, tokens.as_account_id);
    return true;
  } catch {
    return false;
  }
}

/**
 * Right after an SMS login inside Telegram: links this Telegram account, so the next launch signs in by itself. The
 * server accepts it only within minutes of the SMS login; any refusal (linked to someone else, bot off) changes
 * nothing for the customer, who is already signed in.
 */
export function linkTelegramAfterLogin(): void {
  const initData = telegramInitData();
  if (!initData) return;
  linkTelegram({ tl_init_data: initData })
    .then(() => {
      setSignInFallback(signInByLaunchData);
    })
    .catch(() => undefined);
}
